import { useRef, useState } from 'react'

import {
  GAMES,
  TEDDIES,
  TRIES_PER_QUESTION,
  teddyPhotoPath,
  type RiddleView,
  type SkillLevels,
  type TeddyKey,
} from '@hazel/shared'

import { Card } from '../components/Card.js'
import { KeyLegend } from '../components/KeyLegend.js'
import { LEAVE_SHORTCUT } from '../components/shortcuts.js'
import { useBarTitle } from '../layouts/bar-title.js'
import { Announce } from './Announce.js'
import { clueLayout } from './clueLayout.js'
import { LeaveGame } from './LeaveGame.js'
import { Milestone } from './Milestone.js'
import { PlayAgain } from './PlayAgain.js'
import { RaceTrack } from './RaceTrack.js'
import { LANE, RACE_ART, racerPicture } from './raceArt.js'
import { useRiddles } from './useRiddles.js'
import { digitFrom, useGameKeys } from './useGameKeys.js'
import { useLeaveOnEscape } from './useLeaveOnEscape.js'
import { useRound, type DealtRound } from './useRound.js'
import { useAnswerCues, useRoundEndCues, useRoundMusic } from './gameSound.js'
import { useRoundFocus } from './useRoundFocus.js'
import { TRACK_FOR_GAME } from '../sound/cues.js'

/** A teddy's name, from its key. */
function nameOf(teddy: TeddyKey | undefined): string {
  return teddy ? TEDDIES[teddy].name : 'nobody'
}

/**
 * What to say after an answer.
 *
 * The same three goes and the same voice as Monkey's and Turtley's: a wrong
 * answer with goes left is an invitation, not a verdict.
 *
 * The row comes off the answer rather than off the view, because by the time
 * this is read after a third wrong go the row on screen is the next riddle's.
 */
function feedbackFor(view: RiddleView): string {
  const answer = view.lastAnswer
  if (!answer) return ''

  if (answer.correct) return 'Yes!'
  if (answer.moreGoes) return 'Not quite — try again.'

  return `It was ${nameOf(answer.teddies[answer.expected - 1])}. We’ll come back to it.`
}

export function Riddles() {
  // The bar carries the name while she is here, in place of "Hazel's
  // Games". One call rather than one per screen: the chooser and the
  // board are both this component, so the name does not blink out between
  // picking a size and being dealt one.
  useBarTitle(GAMES.riddles.title)

  /*
    The screen's music, not the round's.

    On the round it started when play did — so Sky's was silent through "How
    big a board?", and every game had a beat of nothing while its round was
    being dealt. Asking here means it also survives another go, which is the
    same screen and should not restart the track.
  */
  useRoundMusic(TRACK_FOR_GAME.riddles)

  const { round, again, failed } = useRound<{ levels: SkillLevels }>({ game: 'riddles' })

  if (failed) {
    return (
      <p role="alert" className="text-coral py-16 text-center">
        Rocky couldn’t find his notebook just now. Have another go in a moment.
      </p>
    )
  }

  if (!round) {
    return (
      <p role="status" className="py-16 text-center">
        Rocky is gathering the clues…
      </p>
    )
  }

  return <RiddleRound key={round.seed} round={round} onAgain={again} />
}

function RiddleRound({
  round,
  onAgain,
}: {
  round: DealtRound<{ levels: SkillLevels }>
  onAgain: () => void
}) {
  const leaveOnEscape = useLeaveOnEscape()
  const game = useRiddles(round, onAgain)
  const { view } = game
  const board = useRoundFocus<HTMLDivElement>(round.seed, view.status !== 'playing')

  useAnswerCues(view)
  useRoundEndCues(view.status !== 'playing', game.milestone)

  /**
   * Where the keyboard is pointing along the row.
   *
   * The same idea as Sky's board, for the same reason: on the television there
   * is no mouse, and reading a number off the screen and hunting for the key
   * is a worse way to name somebody than moving to them.
   */
  const [pointingAt, setCursor] = useState(0)

  /** The teddy buttons, so the cursor can carry real focus with it. */
  const teddies = useRef(new Map<number, HTMLButtonElement>())

  /*
   * Clamped where it is read rather than corrected in an effect, which would
   * be a second render to undo the first.
   *
   * A row shorter than the cursor has reached is not reachable today — every
   * riddle in a round has the same number of teddies — but a cursor outliving
   * the row it points into is the kind of thing that stops being impossible
   * quietly.
   */
  const cursor = Math.min(pointingAt, Math.max(0, view.teddies.length - 1))

  function step(by: number): void {
    const next = cursor + by
    // Stops at the ends rather than wrapping. Wrapping loses somebody who is
    // not watching the cursor, which on a television is everybody.
    if (next < 0 || next >= view.teddies.length) return

    setCursor(next)

    /*
     * Focus goes where the ring goes, every time.
     *
     * Without this the ring and the browser's own focus are two separate
     * things, and Enter names whichever teddy was last tabbed to or tapped
     * rather than the one being pointed at. That went wrong first time on
     * Sky's board and reads as perfectly fine until you try it.
     */
    const target = teddies.current.get(next)
    target?.focus()
    if (document.activeElement !== target) board.current?.focus()
  }

  useGameKeys((event) => {
    if (event.key === 'Escape' && view.status !== 'playing') {
      return void leaveOnEscape(false)
    }

    if (view.status !== 'playing') {
      if (event.key === 'Enter') game.again()
      return
    }

    // The seat she means, counting from the front. Kept alongside the cursor
    // rather than replaced by it: the picker taught her that a number opens a
    // thing, and taking that away here would be worse than never having had it.
    const digit = digitFrom(event)
    if (digit) {
      const seat = Number.parseInt(digit, 10)
      if (seat >= 1 && seat <= view.teddies.length) game.answer(seat)
      return
    }

    switch (event.key) {
      case 'ArrowLeft':
        return step(-1)
      case 'ArrowRight':
        return step(1)
      case 'Enter':
      case ' ':
        /*
         * Only reached while focus is on the board itself. With focus on the
         * cursor's teddy the browser presses it — the same teddy, the same
         * answer — which is why `step` moves focus and why up and down are not
         * here: the row is one line that cannot wrap, so left and right are
         * the whole of it.
         */
        event.preventDefault()
        return game.answer(cursor + 1)
      case 'Escape':
        return void leaveOnEscape(false)
      default:
    }
  })

  return (
    <div className="mx-auto flex w-full max-w-xl [[data-scale=tv]_&]:max-w-none flex-col items-center gap-4">
      <Card
        ref={board}
        tabIndex={-1}
        className="outline-none flex w-full flex-col items-center gap-4"
      >
        <p className="text-ink-muted text-(length:--text-small)">
          {`${view.correct} of ${view.roundLength} solved`}
        </p>

        <Announce>
          {[
            view.status !== 'playing'
              ? `${view.correct} out of ${view.roundLength} solved.`
              : feedbackFor(view),
            view.status === 'playing'
              ? `The row: ${view.teddies.map((teddy, seat) => `${seat + 1}, ${nameOf(teddy)}`).join('. ')}. Clues: ${view.clues.join(' ')} ${view.prompt ?? ''}`
              : '',
            game.milestone ? `That’s ${game.milestone} games played.` : '',
          ]
            .filter(Boolean)
            .join(' ')}
        </Announce>

        {/*
          Rocky, following his nose to a bone, with Daddy racing the mistakes on
          the same terms as everywhere else: three wrong answers cost what one
          right answer gains.
        */}
        <RaceTrack
          goal={RACE_ART.bone}
          racers={[
            {
              key: 'rocky',
              name: 'Rocky',
              picture: racerPicture('rocky'),
              steps: view.correct,
              of: view.roundLength,
              tint: LANE.rocky,
            },
            {
              key: 'daddy',
              name: 'Daddy',
              picture: RACE_ART.daddy,
              steps: view.wrongAnswers,
              of: view.roundLength * TRIES_PER_QUESTION,
              tint: LANE.daddy,
            },
          ]}
        />

        {view.status === 'playing' ? (
          <>
            {/*
              The row, drawn, front first — and it is *given*, not deduced.
              Making her work out the seating as well would double the puzzle
              and ask her to hold two hypotheses at once.
            */}
            {/*
              Wider gaps than the rest of the row, so the labels clear the
              cursor's ring.
              
              The ring is drawn outside the button and is the only thing saying
              where the keyboard is pointing — and the ends of the row are
              exactly where somebody arrives after holding left or right until
              it stops, so those are the two places it must not be sat on.
            */}
            <div className="flex w-full items-center justify-center gap-4 [[data-scale=tv]_&]:gap-10 sm:gap-6">
              {/*
                Marked at the ends, not titled above.
                
                The clues say "at the front", "at the back" and "somewhere
                behind", so the row has to say which end is which — and a
                caption centred over the whole thing reads as a heading for the
                list rather than as a label for its left-hand end, which is
                what it needs to be.
                
                The room between them is the flex gap and not padding on the
                labels: padding grows the label's box towards the teddy, so it
                moves the words away and the edge nearer — which measures as
                less room, not more, and the room is what the cursor's ring
                needs.
                
                Silent to a screen reader: the list already announces itself as
                "the row, front first" and each teddy says its own number, so
                reading "Front ... Back" round the outside adds a word without
                adding a fact.
              */}
              <span
                aria-hidden="true"
                className="text-ink-muted shrink-0 text-(length:--text-small)"
              >
                Front
              </span>
              <ol
                aria-label="The row, front first"
                /*
                 * Padded vertically so the cursor's ring is not clipped.
                 * Setting overflow-x alone makes the browser compute
                 * overflow-y as auto too, and the ring is a box-shadow drawn
                 * outside the button's box — with no room for it, the top and
                 * bottom of the ring are cut off.
                 *
                 * Padded on all four sides for the same reason, which took two
                 * goes. A scroll container clips whatever is painted outside
                 * its padding box, in *both* directions, and the ring is 6px
                 * of amber beyond the button. This row is centred, so with
                 * three teddies nothing sits at the edge and nothing is
                 * clipped — but four or five fill the width, the first and
                 * last seats end up flush against the container, and the
                 * outer edge of their ring is shaved into a flat line where
                 * the corner should curve.
                 *
                 * It reads as the Front and Back labels crowding the ring,
                 * which is what it was first reported as. They are forty
                 * pixels away and had nothing to do with it.
                 *
                 * The `2` is tied to the ring being `ring-4 ring-offset-2`,
                 * six pixels, two to spare. If the ring is ever wired to
                 * `--width-focus` and `--offset-focus` — which the television
                 * sets to 8 and 5, thirteen between them — this needs to grow
                 * with it. The television test measures the ring rather than
                 * assuming six, so it will say so; this note is here to save
                 * whoever changes the ring from finding out that way.
                 */
                /*
                 * Centred by the items' own margins, not by `justify-center`.
                 *
                 * Centring a flex row that is *wider* than its box hangs the
                 * content off both ends — and `scrollLeft` starts at 0 and
                 * cannot go negative, so whatever hangs off the left can never
                 * be reached. On a 390px phone that hid 17px of the first
                 * teddy at four in the row and 52px at five, with seat one
                 * reading "ripey" and no way to see the rest of it. Four
                 * arrive at level 3, which is not far into ordinary play.
                 *
                 * Auto margins resolve to zero when there is no free space, so
                 * an overflowing row simply starts at its left edge and scrolls
                 * properly; a row that fits is still centred.
                 */
                className="flex list-none flex-nowrap gap-2 overflow-x-auto p-2 [&>li:first-child]:ms-auto [&>li:last-child]:me-auto"
              >
                {view.teddies.map((teddy, seat) => (
                  /*
                   * Keyed by seat, not by teddy.
                   *
                   * Every riddle draws a different row, so keying by teddy
                   * gives React a wholly new set of keys each question — it
                   * unmounts every button and builds new ones, destroying the
                   * node that held focus. The ring stayed on the right seat
                   * and real focus fell back to the body, which is the two
                   * pointing at different things all over again, one
                   * interaction later than the tests were looking.
                   *
                   * By seat, the buttons persist and their contents change
                   * under them, so focus survives the question changing.
                   */
                  <li key={seat}>
                    <button
                      type="button"
                      ref={(element) => {
                        if (element) teddies.current.set(seat, element)
                        else teddies.current.delete(seat)
                      }}
                      // One tab stop for the whole row, not one per teddy, and
                      // Tab lands where the cursor already is.
                      tabIndex={seat === cursor ? 0 : -1}
                      onClick={(event) => {
                        /*
                         * Focus taken explicitly, because Safari does not give
                         * it to a button on click. Without this a tap moves
                         * the ring while focus stays where it was, and a bare
                         * Enter afterwards answers the old seat.
                         *
                         * Not covered by a test: jsdom focuses on click the
                         * way Chromium does, so the suite cannot tell the two
                         * behaviours apart. It wants a real browser — which is
                         * DOL-46.
                         */
                        event.currentTarget.focus()
                        setCursor(seat)
                        game.answer(seat + 1)
                      }}
                      className={[
                        'border-border hover:border-primary flex min-h-(--size-tap) min-w-(--size-tap)',
                        'flex-col items-center gap-1 rounded-(--radius-control) border-2 p-2',
                        // The cursor is drawn as a ring, outside the button, so
                        // it stays legible from a sofa. The button's own focus
                        // outline is suppressed because focus and the cursor
                        // are always the same teddy — two rings round one
                        // photograph was a real complaint about Sky's board.
                        'outline-none',
                        seat === cursor ? 'ring-focus ring-4 ring-offset-2' : '',
                      ].join(' ')}
                    >
                      {/* Smaller on a phone: five teddies in one unwrapped
                          line is what makes "next to" mean anything. */}
                      <img
                        src={teddyPhotoPath(teddy)}
                        alt=""
                        className="size-10 rounded-full object-cover sm:size-14"
                      />
                      <span className="text-(length:--text-small)">
                        <span className="font-display">{seat + 1}</span> {nameOf(teddy)}
                      </span>
                    </button>
                  </li>
                ))}
              </ol>
              <span
                aria-hidden="true"
                className="text-ink-muted shrink-0 text-(length:--text-small)"
              >
                Back
              </span>
            </div>

            {/*
              Bigger on the television, and only there — but only as big as
              the riddle leaves room for.

              Rocky's row is not split into two columns the way Monkey's,
              Hoppity's and Turtley's are — his input *is* the row of teddies,
              so there is nothing to move to a right-hand side and inventing
              one would be worse than the margins. What was actually wrong was
              simpler: the clues read small from a sofa, and there was room on
              the screen already.

              What that missed is that the room depends on the riddle. This is
              the only thing on any screen here whose height is set by the
              puzzle rather than the design: measured across levels nought to
              ten, a dealt riddle carries anywhere from 68 to 415 characters of
              clue, and at one fixed size the hard ones want more than twice
              the screen.

              So the size follows **how much clue there is, not how many
              clues**. Those are different numbers and the difference matters:
              `riddle-skills.ts` says outright that clue count is not a
              difficulty measure — eight clues can be an easier riddle than
              four — and it is not a height measure either, because a `link`
              clue is a far longer sentence than a `direct` one. An early
              version tiered on the count and two hand-picked seeds passed
              while eleven riddles in sixty overflowed the screen.

              See `clueLayout.ts` for the tiers and why it stops shrinking
              where it does.
            */}
            <ul
              aria-label="The clues"
              className={`border-border flex w-full list-none flex-col gap-2 rounded-(--radius-control) border-2 p-3 [[data-scale=tv]_&]:gap-3 [[data-scale=tv]_&]:p-4 ${clueLayout(view.clues).className}`}
            >
              {view.clues.map((clue) => (
                <li key={clue} className="text-balance">
                  {clue}
                </li>
              ))}
            </ul>

            <div className="flex flex-col items-center gap-3">
              {view.lastAnswer ? (
                <p
                  className={`[[data-scale=tv]_&]:text-(length:--text-heading) ${view.lastAnswer.correct ? 'text-teal' : 'text-ink-muted'}`}
                >
                  {feedbackFor(view)}
                </p>
              ) : null}

              <p className="text-(length:--text-heading) text-center leading-snug text-balance">
                {view.prompt}
              </p>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <p className="font-display text-(length:--text-title)">
              {view.correct === view.roundLength ? 'Case closed!' : 'Good effort!'}
            </p>
            <p className="text-ink-muted [[data-scale=tv]_&]:text-(length:--text-heading)">{`${view.correct} out of ${view.roundLength} solved.`}</p>

            {game.milestone ? <Milestone games={game.milestone} /> : null}

            <PlayAgain label="Another go" does="to get another riddle" onAgain={game.again} />
          </div>
        )}
      </Card>

      <LeaveGame />

      <KeyLegend
        shortcuts={
          view.status === 'playing'
            ? [
                { keys: ['←', '→'], does: 'Move along the row' },
                { keys: ['Enter'], does: 'Say who it was' },
                { keys: ['1', '–', String(view.teddies.length)], does: 'Or name a seat' },
                LEAVE_SHORTCUT,
              ]
            : [{ keys: ['Enter'], does: 'Another go' }, LEAVE_SHORTCUT]
        }
      />
    </div>
  )
}
